import type React from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { updateUser } from "~/api/v2/requests";
import { useUserContext } from "../hooks/useUserContext";
import AvatarWithFallback from "~/molecules/AvatarWithFallback";
import { Button } from "~/atoms/button";
import { OverlaySpinner } from "../atoms/spinner";
import { Input } from "~/atoms/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/atoms/form";
import { Label } from "~/atoms/label";
import i18n from "~/i18n";
import { Badge } from "~/atoms/badge";

const DEFAULT_MAX_FILE_SIZE_IN_BYTES = 1000000;

const ProfileFormSchema = z.object({
  email: z.email({ message: i18n.t(($) => $.validations.email) }),
  firstName: z.string({ message: i18n.t(($) => $.validations.required) }).min(
    1,
    i18n.t(($) => $.validations.required),
  ),
  lastName: z.string({ message: i18n.t(($) => $.validations.required) }).min(
    1,
    i18n.t(($) => $.validations.required),
  ),
  position: z.string().optional(),
});

type ProfileFormValues = z.infer<typeof ProfileFormSchema>;

export const AccountProfile = () => {
  const { user, setUser } = useUserContext();
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);

  const [isLoading, setIsLoading] = useState(false);

  const form = useForm({
    resolver: zodResolver(ProfileFormSchema),
    defaultValues: {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName ?? "",
      position: user.position ?? "",
    },
    mode: "onChange",
  });

  const categories = user.categories?.map((dep) => dep.title);

  const onClickChangeImage = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    fileRef.current?.click();
  };

  const onSubmitImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newImage = event.target.files?.[0];
    if (!newImage) {
      return;
    }
    if (newImage.size > DEFAULT_MAX_FILE_SIZE_IN_BYTES) {
      return toast.warning(t(($) => $.toasts.warning.maxSize));
    }
    setIsLoading(true);
    try {
      const updatedUser = await updateUser(user.id, { file: newImage });
      setUser({ ...user, ...updatedUser });
      toast.success(t(($) => $.toasts.success.photoSaved));
    } catch (err) {
      console.error(err);
      toast.error(t(($) => $.toasts.error.avatar));
    }
    setIsLoading(false);
  };

  const onSubmit = async (values: ProfileFormValues) => {
    try {
      setIsLoading(true);
      const updatedUser = await updateUser(user.id, values);
      setUser({ ...user, ...updatedUser });
      toast.success(t(($) => $.toasts.success.profileUpdated));
    } catch {
      toast.error(t(($) => $.toasts.error.save));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <OverlaySpinner />}
      <input
        ref={fileRef}
        onChange={onSubmitImage}
        accept="image/png, image/jpeg, image/jpg"
        className="hidden"
        type="file"
      />

      <Form {...form}>
        <form className="grid space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid gap-2">
            <Label>{t(($) => $.settings.profile.avatar)}</Label>
            <div className="flex items-center gap-2">
              <AvatarWithFallback user={user} />
              <Button
                type="button"
                onClick={onClickChangeImage}
                variant="secondary"
              >
                {t(($) => $.settings.profile.changeImage)}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(($) => $.settings.profile.firstName)}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t(($) => $.settings.profile.lastName)}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t(($) => $.settings.profile.email)}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="position"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t(($) => $.settings.profile.position)}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-col gap-1">
            <Label>{t(($) => $.commons.document.category)}</Label>
            <div className="flex flex-wrap gap-2">
              {categories?.map((category) => (
                <Badge key={category} variant="secondary">
                  {category}
                </Badge>
              ))}
            </div>
          </div>

          <div className="grid gap-1">
            <Label>{t(($) => $.settings.profile.role)}</Label>
            <div>
              <Badge variant="outline">{user.role}</Badge>
            </div>
          </div>

          <Button type="submit" className="justify-self-end">
            {t(($) => $.commons.buttons.save)}
          </Button>
        </form>
      </Form>
    </>
  );
};
